import { autoinject } from "aurelia-dependency-injection";
import { RequestClientService } from "@rightship-tech/common-ui/services/basic/request-client.service";
import {Urls} from "../../constants/validations/validations.constants";
import { BindingEngine } from "aurelia-binding";
import {
    ValidationResult, GetValidationRulesResponse,
    GetValidationRulesRequest, ChildValidationRequest,
    GetValidationRootEntities, SaveValidationsRequest,
    EntityValidationRule, GetEntityValidationRulesResponse
} from "../../models/validations/validations.models";
import * as Enumerable from "linq";
import { Messages } from "../../configuration/event-messages.constants";
import { NotificationType, NotificationClientService } from "../basics/notification-client.service";
import { EventAggregator } from "aurelia-event-aggregator";
import { I18N } from "aurelia-i18n";

@autoinject
export class ValidationsClientService {

    public _validationRules: { [index: number]: EntityValidationRule[] } = {};
    private _cacheFlag: number[] = []; // if an entitytype is being loaded, the entitytype is added here until loading is complete

    constructor(private requestClient: RequestClientService,
        private notificationService: NotificationClientService,
        private eventAggregator: EventAggregator,
        private i18N: I18N,
        private bindingEngine: BindingEngine) {
        this._validationRules = {};
    }

    public validateChildEntity(request: ChildValidationRequest): Promise<ValidationResult> {
        return this.requestClient.post(Urls.ValidateChildEntity, {}, request);
    }

    public save(request: SaveValidationsRequest): Promise<ValidationResult> {
        return this.requestClient.post(Urls.SaveValidationRulesForLevel, {}, request);
    }

    public validate(request: ChildValidationRequest): Promise<ValidationResult> {
        return this.requestClient.post(Urls.Validate, {}, request);
    }

    public getValidationRulesForLevel(request: GetValidationRulesRequest): Promise<GetValidationRulesResponse> {
        return this.requestClient.post(Urls.GetValidationRulesForLevel, null, request);
    }

    public getValidationRootTypes(): Promise<GetValidationRootEntities> {
        return new Promise<GetValidationRootEntities>((resolve, reject) => {
            this.requestClient.get(Urls.GetValidationRootEntities, null, null).catch(response => {
                resolve(response);
            }).catch(e => {
                reject(e);
            });
        });
    }

    public getValidationRules(entityTypeId: number): Promise<EntityValidationRule[]> {
        return new Promise<EntityValidationRule[]>((resolve, reject) => {
            if (this._validationRules[entityTypeId]) { // data is already loaded
                resolve(this._validationRules[entityTypeId]);
            } else {
                if (this._cacheFlag.indexOf(entityTypeId) !== -1) {// data is being loaded, wait until it is complete
                    const subscription = this.bindingEngine.collectionObserver(this._cacheFlag)
                        .subscribe((newValue: any) => {
                            if (newValue.indexOf(entityTypeId) === -1) {
                                this.getValidationRules(entityTypeId).then((response) => {
                                    resolve(response);
                                });

                                // resolve(this.validationRules[entityType]);
                                subscription.dispose();
                            }
                        });
                } else { //data not available locally, fetch from server
                    this._cacheFlag.push(entityTypeId);
                    this.getEntityValidationRulesInternal(entityTypeId)
                        .then((result: GetEntityValidationRulesResponse) => {
                            this._validationRules[entityTypeId] = result.validationRules;
                            resolve(result.validationRules);
                            this._cacheFlag.splice(this._cacheFlag.indexOf(entityTypeId), 1);
                        }).catch((e) => {
                            reject(e);
                        });
                }
            }
        });
    }

    private getEntityValidationRulesInternal(entityTypeId): Promise<GetEntityValidationRulesResponse> {
        return this.requestClient.get(Urls.GetEntityValidationRules, { entityTypeId });
    }

    public showValidationErrors(validationResult: ValidationResult, prefix: string = null) {
        let fullError = "";
        if (validationResult) {
            Enumerable.from(validationResult.errors).distinct(error => error.message).forEach(error => {

                while (error.message.indexOf('\r\n') > -1) {
                    error.message = error.message.replace('\r\n', '<br/>');
                }

                while (error.message.indexOf('\n') > -1) {
                    error.message = error.message.replace('\n', '<br/>');
                }

                fullError += error.message + "<br/>";
            });

            const message = prefix
                ? (prefix + "<br/>" + fullError)
                : fullError;
            this.notificationService.show(this.i18N.tr('validation-error-text') + "!", message, NotificationType.Error);
        }
        this.eventAggregator.publish(Messages.onValidated, validationResult);
    }

}
